import { BUSINESS, SITE_URL, FAQ_ITEMS, PAGES } from './businessConfig';

const addressSchema = {
  '@type': 'PostalAddress',
  streetAddress: BUSINESS.address.streetAddress,
  addressLocality: BUSINESS.address.addressLocality,
  addressRegion: BUSINESS.address.addressRegion,
  postalCode: BUSINESS.address.postalCode,
  addressCountry: BUSINESS.address.addressCountry,
};

const geoSchema = {
  '@type': 'GeoCoordinates',
  latitude: BUSINESS.geo.latitude,
  longitude: BUSINESS.geo.longitude,
};

export function buildLodgingBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['LodgingBusiness', 'BedAndBreakfast'],
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS.legalName,
    alternateName: BUSINESS.alternateName,
    description: BUSINESS.description,
    url: SITE_URL,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    image: [
      `${SITE_URL}/og-image.jpg`,
      `${SITE_URL}/og-image-alt.jpg`,
      `${SITE_URL}/logo512.png`,
    ],
    logo: `${SITE_URL}/logo512.png`,
    address: addressSchema,
    geo: geoSchema,
    hasMap: `https://www.google.com/maps/search/?api=1&query=${BUSINESS.geo.latitude},${BUSINESS.geo.longitude}`,
    priceRange: BUSINESS.priceRange,
    currenciesAccepted: BUSINESS.currenciesAccepted,
    paymentAccepted: BUSINESS.paymentAccepted.join(', '),
    checkinTime: BUSINESS.checkinTime,
    checkoutTime: BUSINESS.checkoutTime,
    petsAllowed: true,
    smokingAllowed: false,
    numberOfRooms: BUSINESS.rooms.length,
    amenityFeature: BUSINESS.amenities.map((name) => ({
      '@type': 'LocationFeatureSpecification',
      name,
      value: true,
    })),
    areaServed: BUSINESS.areaServed.map((name) => ({
      '@type': 'Place',
      name,
    })),
    sameAs: BUSINESS.sameAs,
    makesOffer: [
      ...BUSINESS.rooms.map((room) => ({
        '@type': 'Offer',
        name: room.name,
        price: room.price,
        priceCurrency: 'PLN',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/cennik`,
        itemOffered: {
          '@type': 'HotelRoom',
          name: room.name,
          occupancy: {
            '@type': 'QuantitativeValue',
            value: room.occupancy,
          },
        },
      })),
      {
        '@type': 'Offer',
        name: BUSINESS.exclusiveOffer.name,
        price: BUSINESS.exclusiveOffer.price,
        priceCurrency: 'PLN',
        url: `${SITE_URL}/cennik`,
        description: `Kaucja ${BUSINESS.exclusiveOffer.deposit} PLN. Wyłączność całego obiektu.`,
      },
    ],
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/kontakt`,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
      result: {
        '@type': 'LodgingReservation',
        name: 'Rezerwacja noclegu',
      },
    },
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: BUSINESS.brandName,
    description: BUSINESS.shortDescription,
    publisher: { '@id': `${SITE_URL}/#business` },
    inLanguage: 'pl-PL',
  };
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: BUSINESS.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: BUSINESS.telephone,
      contactType: 'reservations',
      areaServed: 'PL',
      availableLanguage: ['Polish'],
      email: BUSINESS.email,
    },
    address: addressSchema,
    sameAs: BUSINESS.sameAs,
  };
}

export function buildFaqSchema(items = FAQ_ITEMS) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${SITE_URL}/#faq`,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path === '/' ? '' : crumb.path}`,
    })),
  };
}

export function buildOfferCatalogSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'Cennik noclegów Zacisze Czarna Owca',
    url: `${SITE_URL}/cennik`,
    itemListElement: BUSINESS.rooms.map((room, i) => ({
      '@type': 'Offer',
      position: i + 1,
      name: room.name,
      price: room.price,
      priceCurrency: 'PLN',
      url: `${SITE_URL}/cennik`,
    })),
  };
}

export function buildTouristAttractionExtras() {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name: `${BUSINESS.brandName} — atrakcje rekreacyjne`,
    description:
      'Basen, sauna, jacuzzi, kort tenisowy i sala gier dostępne dla gości agroturystyki w Brzeżance koło Strzyżowa.',
    url: SITE_URL,
    address: addressSchema,
    geo: geoSchema,
    isAccessibleForFree: false,
    touristType: ['Family', 'Couples', 'Groups'],
  };
}

/** Full graph for homepage (and static index.html mirror). */
export function buildHomeGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildLodgingBusinessSchema(),
      buildWebSiteSchema(),
      buildOrganizationSchema(),
      buildFaqSchema(),
      buildBreadcrumbSchema([{ name: 'Strona główna', path: '/' }]),
    ],
  };
}

export function pageBreadcrumbs(pageKey) {
  const page = PAGES[pageKey];
  if (!page || pageKey === 'home') {
    return buildBreadcrumbSchema([{ name: 'Strona główna', path: '/' }]);
  }
  return buildBreadcrumbSchema([
    { name: 'Strona główna', path: '/' },
    { name: page.title.split('|')[0].trim(), path: page.path },
  ]);
}
